import { useEffect, useState } from "react";
import { Trash2, Pencil, Plus, Info } from "lucide-react";
import AddSchoolModal from "../../modals/AddSchoolModal";
import EditSchoolModal from "../../modals/EditSchoolModal";
import ConfirmDeleteSchoolModal from "../../modals/ConfirmDeleteSchoolModal";
import useUserStore from "../../../stores/user";
import supabase from "../../../utils/supabase";

interface School {
  id: string;
  name: string;
  user_id: string;
  created_at: string;
  district_id: string;
}

export default function Schools() {
  const [schools, setSchools] = useState<School[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDistrictLoading, setIsDistrictLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const { profile } = useUserStore();
  const [district, setDistrict] = useState<{ id: string; name: string } | null>(null);

  async function fetchDistrict() {
    if (!profile?.district) {
      setIsDistrictLoading(false);
      return;
    }
    setIsDistrictLoading(true);
    const { data, error } = await supabase
      .from("districts")
      .select("id, name")
      .eq("id", profile.district)
      .maybeSingle();
    if (error) console.error("Failed to fetch district:", error);
    setDistrict(data);
    setIsDistrictLoading(false);
  }

  async function fetchSchools(search?: string) {
    if (!profile?.district) {
      setSchools([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    let query = supabase
      .from("schools")
      .select("id, name, user_id, created_at, district_id")
      .eq("district_id", profile.district)
      .order("created_at", { ascending: false });

    if (search) {
      query = query.ilike("name", `%${search}%`);
    }

    const { data, error } = await query;
    if (error) {
      console.error("Failed to fetch schools:", error);
      setIsLoading(false);
      return;
    }
    setSchools(data ?? []);
    setIsLoading(false);
  }

  useEffect(() => {
    fetchDistrict();
  }, [profile]);

  useEffect(() => {
    const id = setTimeout(() => fetchSchools(searchTerm), searchTerm ? 400 : 0);
    return () => clearTimeout(id);
  }, [searchTerm, profile?.district]);

  const handleEditClick = (school: School) => {
    setSelectedSchool(school);
    setIsEditOpen(true);
  };

  const handleDeleteClick = (school: School) => {
    setSelectedSchool(school);
    setIsDeleteOpen(true);
  };

  return (
    <div>
      <AddSchoolModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        districtId={profile?.district}
        districtName={district?.name}
        onSuccess={() => {
          setIsAddOpen(false);
          fetchSchools(searchTerm);
        }}
      />

      <EditSchoolModal
        isOpen={isEditOpen}
        onClose={() => {
          setIsEditOpen(false);
          setSelectedSchool(null);
        }}
        school={selectedSchool}
        onSuccess={() => {
          setIsEditOpen(false);
          setSelectedSchool(null);
          fetchSchools(searchTerm);
        }}
      />

      <ConfirmDeleteSchoolModal
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setSelectedSchool(null);
        }}
        school={selectedSchool}
        onSchoolDeleted={() => {
          setIsDeleteOpen(false);
          setSelectedSchool(null);
          fetchSchools(searchTerm);
        }}
      />

      {/* Header */}
      <div className="bg-white border-b shadow-sm p-6 flex justify-between gap-5 items-start lg:items-center flex-col lg:flex-row">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Schools</h1>
          <p className="text-sm text-gray-600 mt-1">
            District:{" "}
            {isDistrictLoading
              ? "Loading..."
              : district?.name || "Not assigned"}
          </p>
          {!district?.name && !isDistrictLoading && (
            <p className="text-sm text-yellow-600 mt-1 flex items-center gap-1">
              <Info className="w-4 h-4" />
              Please contact Project Explore <b>Super Admin</b> to get assigned
              to a district.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsAddOpen(true)}
            disabled={!district?.name}
            className="btn btn-small"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add School
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto py-6 px-4 bg-gray-50">
        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Schools
              </label>
              <input
                type="text"
                className="w-full p-2 border rounded-lg"
                placeholder="Search by name"
                value={searchTerm}
                disabled={!district?.name || isDistrictLoading}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    School Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="text-center text-sm text-gray-500 py-6 italic"
                    >
                      Loading schools...
                    </td>
                  </tr>
                ) : schools.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="text-center text-sm text-gray-500 py-6"
                    >
                      {searchTerm
                        ? "No schools found matching your search."
                        : "No schools found."}
                    </td>
                  </tr>
                ) : (
                  schools.map((school) => (
                    <tr key={school.id}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {school.name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">
                          {new Date(school.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleEditClick(school)}
                            className="text-gray-400 hover:text-indigo-600 transition-colors"
                            title="Edit School"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(school)}
                            className="text-red-400 hover:text-red-600 transition-colors"
                            title="Delete School"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
